<template>
  <div class="bank-component-wrapper">
    <div class="floating-panel">
      <!-- Vista compacta -->
      <div v-if="!panelExpanded" class="compact-view">
        <v-btn icon small color="#1976D2" class="mb-1 icon-btn" @click.stop="openBankInfo">
          <v-icon class="white--text">mdi-bank</v-icon>
        </v-btn>
        <v-btn icon small color="#4CAF50" class="icon-btn" @click.stop="goToAddFunds">
          <v-icon class="white--text">mdi-plus-circle</v-icon>
        </v-btn>
        <v-btn icon small color="#2196F3" class="mt-2 expand-button" @click.stop="expandPanel">
          <v-icon class="white--text">mdi-chevron-right</v-icon>
        </v-btn>
      </div>

      <!-- Vista expandida -->
      <transition name="slide">
        <div
          v-if="panelExpanded"
          class="expanded-view"
          @mouseenter="cancelAutoCollapse"
          @mouseleave="startAutoCollapse"
        >
          <div class="action-buttons">
            <v-btn small color="#1976D2" class="mb-2" @click.stop="openBankInfo">
              <span class="white--text">Info Bancaria</span>
              <v-icon right class="white--text">mdi-bank</v-icon>
            </v-btn>

            <v-btn small color="#4CAF50" class="mb-2" @click.stop="goToAddFunds">
              <span class="white--text">Agregar Fondos</span>
              <v-icon right class="white--text">mdi-plus-circle</v-icon>
            </v-btn>

            <v-btn icon small color="#9E9E9E" class="mt-2" @click.stop="collapsePanel">
              <v-icon class="white--text">mdi-chevron-left</v-icon>
            </v-btn>
          </div>
        </div>
      </transition>
    </div>

    <!-- Diálogo con info bancaria -->
    <v-dialog v-model="dialogVisible" max-width="500" scrollable @keydown.esc="dialogVisible = false">
      <v-card>
        <v-card-title class="color px-5 py-3">
          <h2 class="headline">Datos Bancarios</h2>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogVisible = false">
            <v-icon class="color">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0 mx-0">
          <v-tabs v-model="activeCurrency" color="primary">
            <v-tab v-for="currency in currenciesVisible" :key="currency.value">
              {{ currency.text }}
            </v-tab>

            <v-tab-item v-for="currency in currenciesVisible" :key="currency.value">
              <v-list three-line>
                <template v-for="(method, index) in paymentMethodsVisible[currency.value]">
                  <v-list-item :key="method.name">
                    <v-list-item-avatar>
                      <v-img :src="method.icon" :alt="`Logo ${method.name}`" contain />
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">{{ method.name }}</v-list-item-title>
                      <template v-for="(detail, i) in method.details">
                        <v-list-item-subtitle :key="i">{{ detail }}</v-list-item-subtitle>
                      </template>
                    </v-list-item-content>

                    <v-list-item-action>
                      <!-- Copiar limpio -->
                      <v-btn
                        v-if="method.copyable"
                        icon
                        @click="copyToClipboard(method.details)"
                        :aria-label="`Copiar ${method.name}`"
                      >
                        <v-icon>mdi-content-copy</v-icon>
                      </v-btn>

                      <!-- Conciliar: SOLO Pago Movil (Venezuela) en VES -->
                      <v-btn
                        v-if="currency.value === 'ves' && method.name === 'Pago Movil (Venezuela)'"
                        class="ml-2 primary-cta primary-cta--sm"
                        @click="openReconcileForm(method)"
                      >
                        Ya Pagué
                        <v-icon right>mdi-cursor-default-click</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>

                  <v-divider
                    v-if="index < paymentMethodsVisible[currency.value].length - 1"
                    :key="`divider-${index}`"
                  />
                </template>
              </v-list>
            </v-tab-item>
          </v-tabs>
        </v-card-text>

        <v-card-actions class="px-4 py-3">
          <v-spacer></v-spacer>
          <v-btn color="color" @click="dialogVisible = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Formulario de Conciliación -->
    <v-dialog v-model="reconcileDialog" max-width="620" persistent>
      <v-card class="reconcile-card">
        <div class="reconcile-hero">
          <div class="reconcile-hero-content">
            <v-icon>mdi-clipboard-check-outline</v-icon>
            <div>
              <div class="reconcile-title">Conciliar Pago Móvil</div>
              <div class="reconcile-subtitle">Completa los datos de tu operación</div>
            </div>
          </div>
          <v-btn icon dark @click="closeReconcileForm"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="px-5 pt-5 pb-1">
          <!-- ALERTA dentro del form (éxito o error) -->
          <v-alert
            v-if="formAlert.show"
            :type="formAlert.type"
            dense
            outlined
            class="mb-4"
            transition="fade-transition"
          >
            {{ formAlert.text }}
          </v-alert>

          <v-form ref="reconcileForm" v-model="reconcileValid" lazy-validation @keydown.enter.prevent="submitReconcileForm">
            <v-row dense>
              <v-col cols="3" sm="2">
                <v-select
                  v-model="form.cedPrefix"
                  :items="cedPrefixes"
                  label="Pref."
                  outlined
                  dense
                  hide-details="auto"
                  class="mini-field"
                  :disabled="loadingSubmit || successMode"
                />
              </v-col>
              <v-col cols="9" sm="4">
                <v-text-field
                  v-model="form.cedNumber"
                  label="Cédula"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required, rules.numbersOnly]"
                  maxlength="10"
                  prepend-inner-icon="mdi-card-account-details-outline"
                  :disabled="loadingSubmit || successMode"
                  @keypress="allowOnlyDigits"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.telefonoPagador"
                  label="Teléfono pagador"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required, rules.phoneVzla]"
                  hint="0XXXXXXXXXX"
                  persistent-hint
                  prepend-inner-icon="mdi-phone"
                  :disabled="loadingSubmit || successMode"
                  @keypress="allowOnlyDigits"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="form.bancoOrigen"
                  :items="bankItems"
                  item-text="text"
                  item-value="value"
                  label="Banco origen"
                  outlined
                  dense
                  hide-details="auto"
                  clearable
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-bank"
                  :disabled="loadingSubmit || successMode"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.referencia"
                  label="Referencia"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required, rules.ref]"
                  maxlength="20"
                  prepend-inner-icon="mdi-pound"
                  :disabled="loadingSubmit || successMode"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.fechaPago"
                  label="Fecha de pago"
                  type="date"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-calendar-blank-outline"
                  :disabled="loadingSubmit || successMode"
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="form.importe"
                  label="Importe (ej: 120.00)"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required, rules.amountPositive]"
                  hint="Usa punto como separador decimal"
                  persistent-hint
                  prepend-inner-icon="mdi-currency-usd"
                  :disabled="loadingSubmit || successMode"
                  @blur="normalizeAmount"
                  @keypress="allowOnlyAmount"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider class="soft-divider"></v-divider>

        <v-card-actions class="px-5 py-4">
          <v-spacer></v-spacer>

          <!-- Footer en modo éxito: SOLO Cerrar -->
          <template v-if="successMode">
            <v-btn class="primary-cta" depressed dark @click="closeReconcileForm">Cerrar</v-btn>
          </template>

          <!-- Footer normal: Cancelar + Enviar -->
          <template v-else>
            <v-btn text class="cancel-ghost" @click="closeReconcileForm">Cancelar</v-btn>
            <v-btn
              class="primary-cta"
              depressed
              dark
              :loading="loadingSubmit"
              :disabled="!reconcileValid || loadingSubmit"
              @click="submitReconcileForm"
            >
              <span v-if="!loadingSubmit">Enviar conciliación</span>
              <span v-else>Enviando…</span>
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar (opcional) -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="2500" top right>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "BankInfoPanel",
  data() {
    return {
      dialogm1: 0,
      dialog: true,
      dialogMC: false,
      panelExpanded: false,
      dialogVisible: false,
      activeCurrency: 0,
      autoCollapseTimer: null,

      // Filtros por país
      hiddenTabsByCountry: { Venezuela: ["cop", "pen"] },
      hiddenAccountsByCountry: {
        Venezuela: { usd: ["Paypal", "Binance Pay", "Zinli", "Venmo"], cop: [], ves: [], pen: [] },
      },

      // Catálogos
      currencies: [
        { text: "USD", value: "usd" },
        { text: "VES", value: "ves" },
        { text: "COP", value: "cop" },
        { text: "PEN", value: "pen" },
      ],
      paymentMethods: {
        usd: [
          { name: "Zelle", icon: "https://trackandraces.com/cuentas/zelle.png", details: ["abonoonlinetr@gmail.com"], copyable: false },
          { name: "Venmo", icon: "https://trackandraces.com/cuentas/venmo.png", details: ["@trabonoonline"], copyable: false },
          { name: "Paypal", icon: "https://trackandraces.com/cuentas/paypal.png", details: ["AbonoonlineTR@gmail.com"], copyable: false },
          { name: "Zinli", icon: "https://trackandraces.com/cuentas/zinli.png", details: ["clecov@gmail.com"], copyable: false },
          { name: "Binance Pay", icon: "https://trackandraces.com/img/binancepay.webp", details: ["clecov@gmail.com"], copyable: false },
        ],
        ves: [
          { name: "Bancamiga", icon: "https://trackandraces.com/cuentas/banktranfer.png", details: ["Nombre: Inversiones Track and Races, C.A","Cuenta: 0172 0117 4111 8251", "RIF: J500728239"], copyable: false },
          { name: "Pago Movil (Bancamiga)", icon: "https://trackandraces.com/cuentas/pagomovil.png", details: ["Banco: Bancamiga","Telefono: 0412 270 73 95", "RIF: J500728239"], copyable: false },
          { name: "Pago Movil (Venezuela)", icon: "https://trackandraces.com/cuentas/pagomovil.png", details: ["Banco: Venezuela","Telefono: 0424 898 82 84", "RIF: J500728239"], copyable: true },
        ],
        cop: [
          { name: "NEQUI", icon: "https://assets-global.website-files.com/6317a229ebf7723658463b4b/631b991aff307a018c353dcd_Logo-Nequi.svg", details: ["57-3015420990"], copyable: true },
          { name: "DAVIPlata", icon: "https://trackandraces.com/cuentas/daviplata3.png", details: ["57-3023657173"], copyable: true },
          { name: "Banco de Bogota (Ahorro)", icon: "https://trackandraces.com/cuentas/banktranfer.png", details: ["Nombre: Rhonny Diaz","Cuenta: 3871565161", "Cedula: 1127621393"], copyable: false },
          { name: "Bancolombia (Ahorro)", icon: "https://trackandraces.com/cuentas/banktranfer.png", details: ["Nombre: Kimberly Marcano","Cuenta: 48100004911", "Cedula: 5001241572"], copyable: false },
        ],
        pen: [
          { name: "YAPE", icon: "https://trackandraces.com/cuentas/yape.png", details: ["Nombre: SYELTECH S.A.C","Cuenta: 51-917073520"], copyable: false },
          { name: "PLIN", icon: "https://trackandraces.com/cuentas/plim.png", details: ["Nombre: Mayuliz Perez","Cuenta: 51-933292975"], copyable: false },
          { name: "BCP", icon: "https://trackandraces.com/cuentas/banktranfer.png", details: ["Nombre: SYELTECH S.A.C","Cuenta: 194-8967899-0-35"], copyable: false },
          { name: "BBVA", icon: "https://trackandraces.com/cuentas/banktranfer.png", details: ["Nombre: SYELTECH S.A.C","Cuenta: 0011-0137-0100052348"], copyable: false },
          { name: "INTERBANK", icon: "https://trackandraces.com/cuentas/banktranfer.png", details: ["Nombre: SYELTECH S.A.C","Cuenta: 3683003133099"], copyable: false },
          { name: "SCOTIABANK", icon: "https://trackandraces.com/cuentas/banktranfer.png", details: ["Nombre: Mayuliz Perez","Cuenta: 074-7481992"], copyable: false },
        ],
      },

      // Banco origen (selector)
      bankItems: [
        { value: "0102", text: "0102 — BANCO DE VENEZUELA" },
        { value: "0104", text: "0104 — BANCO VENEZOLANO DE CREDITO" },
        { value: "0105", text: "0105 — BANCO MERCANTIL" },
        { value: "0108", text: "0108 — BBVA PROVINCIAL" },
        { value: "0114", text: "0114 — BANCARIBE" },
        { value: "0115", text: "0115 — BANCO EXTERIOR" },
        { value: "0128", text: "0128 — BANCO CARONI" },
        { value: "0134", text: "0134 — BANESCO" },
        { value: "0137", text: "0137 — BANCO SOFITASA" },
        { value: "0138", text: "0138 — BANCO PLAZA" },
        { value: "0146", text: "0146 — BANGENTE" },
        { value: "0151", text: "0151 — BANCO FONDO COMUN" },
        { value: "0156", text: "0156 — 100% BANCO" },
        { value: "0157", text: "0157 — DELSUR BANCO UNIVERSAL" },
        { value: "0163", text: "0163 — BANCO DEL TESORO" },
        { value: "0168", text: "0168 — BANCRECER" },
        { value: "0169", text: "0169 — R4 BANCO MICROFINANCIERO C.A." },
        { value: "0171", text: "0171 — BANCO ACTIVO" },
        { value: "0172", text: "0172 — BANCAMIGA BANCO UNIVERSAL, C.A." },
        { value: "0173", text: "0173 — BANCO INTERNACIONAL DE DESARROLLO" },
        { value: "0174", text: "0174 — BANPLUS" },
        { value: "0175", text: "0175 — BANCO DIGITAL DE LOS TRABAJADORES, BANCO UNIVERSAL" },
        { value: "0177", text: "0177 — BANFANB" },
        { value: "0178", text: "0178 — N58 BANCO DIGITAL BANCO MICROFINANCIERO S A" },
        { value: "0191", text: "0191 — BANCO NACIONAL DE CREDITO" },
      ],

      // Diálogo de conciliación
      reconcileDialog: false,
      reconcileValid: false,
      cedPrefixes: ["V", "E", "J", "G", "P"],
      form: {
        cedPrefix: "V",
        cedNumber: "",
        telefonoPagador: "",
        referencia: "",
        fechaPago: "",
        importe: "",
        bancoOrigen: "0102",
      },
      hidden: {
        telefonoDestino: "",
        reqCed: true,
      },

      // Alert en formulario + Snackbar + loading + success
      formAlert: { show: false, type: "info", text: "" },
      snackbar: { show: false, text: "", color: "success" },
      loadingSubmit: false,
      successMode: false,

      // Reglas
      rules: {
        required: v => !!v || "Requerido",
        numbersOnly: v => (!v || /^\d+$/.test(v)) || "Solo números",
        phoneVzla: v => (!v || /^0\d{10}$/.test(v)) || "Formato: 0XXXXXXXXXX (11 dígitos)",
        ref: v => (!v || /^[A-Za-z0-9]{6,20}$/.test(v)) || "6–20 caracteres alfanuméricos",
        amountPositive: v => {
          if (!v) return "Requerido";
          const ok = /^\d+(\.\d{1,2})?$/.test(v) && parseFloat(v) > 0;
          return ok || "Monto inválido. Ej: 120 (> 0)";
        },
      },
    };
  },
  computed: {
    currenciesVisible() {
      const country = this.$store && this.$store.state && this.$store.state.pais_user;
      const hidden = (this.hiddenTabsByCountry[country] || []).map(v => v.toLowerCase());
      const visible = this.currencies.filter(c => !hidden.includes(c.value.toLowerCase()));
      return visible.length ? visible : this.currencies;
    },
    paymentMethodsVisible() {
      const country = this.$store && this.$store.state && this.$store.state.pais_user;
      const byCurrency = (this.hiddenAccountsByCountry[country] || {});
      const result = {};
      for (const cur of this.currencies) {
        const hideNames = (byCurrency[cur.value] || []);
        result[cur.value] = (this.paymentMethods[cur.value] || []).filter(m => !hideNames.includes(m.name));
      }
      return result;
    },
  },
  watch: {
    'form.bancoOrigen'(val) {
      this.hidden.reqCed = String(val) === '0102';
    }
  },
  methods: {
    openBankInfo() {
      this.dialogVisible = true;
      this.collapsePanel();
    },
    goToAddFunds() {
      this.$store.state.dialogRe = true;
      this.$router.push('/profile');
      this.collapsePanel();
    },
    expandPanel() {
      this.panelExpanded = true;
      this.startAutoCollapse();
    },
    collapsePanel() {
      this.panelExpanded = false;
      this.clearAutoCollapse();
    },
    startAutoCollapse() {
      this.clearAutoCollapse();
      this.autoCollapseTimer = setTimeout(() => {
        this.panelExpanded = false;
      }, 5000);
    },
    cancelAutoCollapse() {
      this.clearAutoCollapse();
    },
    clearAutoCollapse() {
      if (this.autoCollapseTimer) {
        clearTimeout(this.autoCollapseTimer);
        this.autoCollapseTimer = null;
      }
    },

    // Copiar limpio
    copyToClipboard() {
  const text = "0102 04248988284 J500728239";

  navigator.clipboard.writeText(text)
    .then(() => {
      this.snackbar = {
        show: true,
        text: "Número copiado al portapapeles",
        color: "success"
      };
    })
    .catch(() => {
      this.snackbar = {
        show: true,
        text: "Error al copiar el número",
        color: "error"
      };
    });
},

    // Solo dígitos
    allowOnlyDigits(e) {
      const ch = String.fromCharCode(e.which || e.keyCode);
      if (!/[0-9]/.test(ch)) e.preventDefault();
    },
    // Dígitos + punto (para importe)
    allowOnlyAmount(e) {
      const ch = String.fromCharCode(e.which || e.keyCode);
      if (!/[0-9.]/.test(ch)) e.preventDefault();
    },
    normalizeAmount() {
      if (this.form.importe && /^\d+(\.\d{1,2})?$/.test(this.form.importe)) {
        this.form.importe = Number(this.form.importe).toFixed(2);
      }
    },

    // ---- Reset controlado del formulario ----
    resetForm() {
      // valores por defecto
      this.form = {
        cedPrefix: "V",
        cedNumber: "",
        telefonoPagador: "",
        referencia: "",
        fechaPago: "",
        importe: "",
        bancoOrigen: "0102",
      };
      this.hidden.telefonoDestino = "";
      this.hidden.reqCed = true;
      this.successMode = false;
      this.formAlert = { show: false, type: "info", text: "" };

      // limpiar validaciones
      this.$nextTick(() => {
        if (this.$refs.reconcileForm) {
          // resetea mensajes y estado de validación
          this.$refs.reconcileForm.resetValidation();
        }
      });
    },

    // Abrir form conciliación (prellenar ocultos y SIEMPRE resetear antes)
    openReconcileForm(method) {
      // 1) reset duro antes de abrir, para que no queden datos previos
      this.resetForm();

      // 2) calcular teléfono destino desde el método seleccionado
      const text = Array.isArray(method.details) ? method.details.join(" ") : String(method.details || "");
      const match = text.match(/Tel(?:e|é)fono:\s*([+\d][\d\s().-]+)/i);
      const telefonoDestino = match ? match[1].replace(/\D/g, "") : "";

      // 3) fecha por defecto (hoy)
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");

      this.hidden.telefonoDestino = telefonoDestino || "";
      this.hidden.reqCed = String(this.form.bancoOrigen) === '0102';
      this.form.fechaPago = `${yyyy}-${mm}-${dd}`;

      this.reconcileDialog = true;
    },

    closeReconcileForm() {
      this.reconcileDialog = false;
      // limpiar todo al cerrar, para que la próxima apertura esté vacío
      this.resetForm();
    },

    // Envío directo y mapeo según { raw: { code, message, data, status } }
    submitReconcileForm() {
      if (!this.$refs.reconcileForm.validate()) return;

      this.loadingSubmit = true;
      this.formAlert = { show: false, type: "info", text: "" };

      try {
        // Normalizaciones
        const telefonoPagador = (this.form.telefonoPagador || "").replace(/\D/g, "");
        const telefonoDestino = (this.hidden.telefonoDestino || "").replace(/\D/g, "");
        const importe = Number(this.form.importe).toFixed(2);
        const cedulaPagador = `${this.form.cedPrefix}${this.form.cedNumber}`;
        const reqCed = String(this.form.bancoOrigen) === "0102";

        const payload = {
          cedulaPagador,
          telefonoPagador,
          telefonoDestino,
          referencia: this.form.referencia,
          fechaPago: this.form.fechaPago,
          importe,
          bancoOrigen: this.form.bancoOrigen,
          reqCed,
        };

        axios.post("https://apitr.xyz/onlineapi/api/dev/pasarela/pmvzla", payload, {
          timeout: 15000,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        })
        .then((response) => {
          // { raw: { code, message, data: { status, amount, reason, referencia }, status: 200 } }
          const root = response?.data || {};
          const raw  = root?.raw || root; // fallback si viene sin 'raw'
          const code = String(raw?.code ?? "");
          const d    = raw?.data || {};
          const reason = d?.reason || raw?.message || "Respuesta del banco";
          const ref  = d?.referencia ? ` • Ref: ${d.referencia}` : "";
          const amt  = d?.amount ? ` • Monto: ${d.amount}` : "";

          if (code === "1000") {
            // Éxito (transacción correcta): activar modo éxito
            this.successMode = true;
            this.formAlert = { show: true, type: "success", text: `${reason}${ref}${amt}` };
          } else {
            // Error de negocio
            this.formAlert = { show: true, type: "error", text: `${reason}${ref}${code ? ` • Estatus: ${code}` : ""}` };
          }
        })
        .catch((err) => {
          const api  = err?.response?.data || {};
          const raw  = api?.raw || api;
          const code = String(raw?.code ?? "");
          const d    = raw?.data || {};
          const ref  = d?.referencia ? ` • Ref: ${d.referencia}` : "";
          const reasonFromData   = d?.reason;
          const reasonFromRawMsg = raw?.message;
          const fallbackMsg = (typeof api?.message === "string" ? api.message : null) || err.message || "No se pudo conciliar";

          // Errores de validación (422) desde tu backend
          if (err?.response?.status === 422 && api?.errors) {
            const firstField = Object.keys(api.errors)[0];
            const firstMsg = api.errors[firstField]?.[0] || fallbackMsg;
            this.formAlert = { show: true, type: "error", text: firstMsg };
          } else {
            const msg = reasonFromData || reasonFromRawMsg || fallbackMsg;
            this.formAlert = { show: true, type: "error", text: `${msg}${ref}${code ? ` • Estatus: ${code}` : ""}` };
          }
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
      } catch (e) {
        console.error(e);
        this.formAlert = { show: true, type: "error", text: "No se pudo preparar el envío" };
        this.loadingSubmit = false;
      }
    },
  },
};
</script>

<style scoped>
/* Panel flotante */
.bank-component-wrapper {
  position: fixed;
  bottom: 180px;
  left: 0;
  z-index: 9999;
}
.floating-panel {
  background: #F44336;
  border-radius: 0 8px 0 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  width: auto;
  padding: 1;
}
.compact-view,
.expanded-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
}
.action-buttons {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

/* Transición */
.slide-enter-active,
.slide-leave-active { transition: opacity 0.3s; }
.slide-enter,
.slide-leave-to { opacity: 0; }

/* —— Estética pro del diálogo —— */
.reconcile-card {
  border-radius: 14px;
  overflow: hidden;
}
.reconcile-hero {
  background: #0F172A; /* Slate-900 */
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
}
.reconcile-hero-content {
  display: flex;
  align-items: center;
  gap: 10px;
}
.reconcile-title {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: .3px;
}
.reconcile-subtitle {
  opacity: .8;
  font-size: .78rem;
}

/* Inputs más pequeños, estilo minimal */
.mini-field { max-width: 70px; }
.soft-divider { border-color: #edf0f4 !important; }

/* Tipografías compactas (Vuetify 2) */
::v-deep .v-label { font-size: 11.5px; }
::v-deep .v-input--dense input { font-size: 12.5px; }
::v-deep .v-select__selections { font-size: 12.5px; }
::v-deep .v-text-field input { letter-spacing: .1px; }

::v-deep .v-input--dense .v-input__slot { border-radius: 10px; }
::v-deep .v-messages__message { font-size: 11px; }
::v-deep .v-icon { font-size: 18px; }

/* Botones */
.primary-cta {
  background: #16a34a;  /* verde elegante */
  color: #fff !important;
  border-radius: 999px;
  padding: 10px 18px;
  text-transform: none;
  font-weight: 700;
  letter-spacing: .2px;
  box-shadow: 0 8px 18px rgba(22,163,74,.25);
}
.primary-cta--sm {
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 999px;
  box-shadow: 0 6px 14px rgba(22,163,74,.22);
  line-height: 1;
}
::v-deep .primary-cta.v-btn { background-color: #16a34a !important; color: #ffffff !important; }
::v-deep .primary-cta.v-btn:hover { filter: brightness(0.97); }
::v-deep .primary-cta.v-btn:active { transform: translateY(0.5px); }
::v-deep .primary-cta.v-btn.v-btn--disabled {
  background-color: #93e3b1 !important;
  color: #ffffffcc !important;
  box-shadow: none;
}
.cancel-ghost {
  color: #6b7280 !important;
  text-transform: none;
  font-weight: 600;
}

/* Placeholder más tenue */
::v-deep input::placeholder { color: #9aa3af; opacity: 1; }
</style>
